// SlugEditor.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Editor from '@react-page/editor';
import api from './util/api';

// your plugins
import createSlatePlugin from '@react-page/plugins-slate';
import { createFilePlugin, FileUploadFunction } from './plugins/createFilePlugin';
import { createCustomImagePlugin, ImageUploadFunction } from './plugins/customImagePlugin';
import { createParallaxContainerPlugin } from './plugins/createParallaxContainerPlugin';
import { createSimpleContainerPlugin } from './plugins/simpleContainerPlugin';
import { createHrPlugin } from './plugins/createHrPlugin';
import { PageJSON, isValidPage } from "./PageView";
import { BulkImageUpload } from './components/BulkImageUpload';
import { createCarouselPlugin } from './plugins/carouselPlugin';
import { ToastContainer, ToastMessage } from './components/Toast';

import colorPlugin from './plugins/colorPlugin';

/**
 * In React Page, you define an array of "cell plugins".
 * Each plugin handles a specific type of content (text, image, etc.).
 */
const slatePlugin = createSlatePlugin(def => ({
  ...def,
  plugins: {
    alignment: def.plugins.alignment,
    code: def.plugins.code,
    emphasize: def.plugins.emphasize,
    headings: def.plugins.headings,
    link: def.plugins.link,
    paragraphs: def.plugins.paragraphs,
    color: {
      SetColor: colorPlugin,
    },
    lists: def.plugins.lists,
  },
}));

const hrPlugin = createHrPlugin();
const parallaxContainerPlugin = createParallaxContainerPlugin();
const simpleContainerPlugin = createSimpleContainerPlugin();

const uploadFileToServer: FileUploadFunction = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/api/upload-file/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return { url: res.data.fileUrl };
};

const uploadImageToServer: ImageUploadFunction = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/api/upload-file/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return { url: res.data.fileUrl };
};

const filePlugin = createFilePlugin(uploadFileToServer);
const customImagePlugin = createCustomImagePlugin(uploadImageToServer);
const carouselPlugin = createCarouselPlugin(uploadImageToServer);

const cellPlugins = [slatePlugin, customImagePlugin, carouselPlugin, filePlugin, hrPlugin, parallaxContainerPlugin, simpleContainerPlugin];

export function SlugEditor() {
  const { slug } = useParams();  // e.g. 'home-page'
  const [pageData, setPageData] = useState<any>(null);
  const [backupData, setBackupData] = useState<any>(null);
  const [title, setTitle] = useState<string>('');
  const [savedTitle, setSavedTitle] = useState<string>('');
  const [savedContentStr, setSavedContentStr] = useState<string | null>(null);

  const [headder, setHeader] = useState<any>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [status, setStatus] = useState<'saved' | 'unsaved' | 'preview'>('saved');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const cacheKey = slug ? (slug === 'page-header' ? null : 'page-header') : null;

  const addToast = (type: ToastMessage['type'], text: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, text }]);
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    if (!cacheKey) return;

    const cachedRaw = localStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (isValidPage(cached)) {
          setHeader(cached);
        } else {
          localStorage.removeItem(cacheKey);
        }
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }

    api
      .get<PageJSON>(`/api/pages/slug/${cacheKey}/`)
      .then((res) => {
        if (isValidPage(res.data)) {
          setHeader(res.data);
          localStorage.setItem(cacheKey, JSON.stringify(res.data));
        }
      })
      .catch((err) => console.error(err));
  }, [cacheKey]);

  // Load the page data from Django by slug
  useEffect(() => {
    if (!slug) return;
    api.get(`/api/pages/slug/${slug}/`).then((res) => {
      const initialContent = res.data.draft_content || res.data.content || null;
      const initialTitle = res.data.title || '';
      setPageData(initialContent);
      setBackupData(initialContent);
      setTitle(initialTitle);
      setSavedTitle(initialTitle);
      setSavedContentStr(initialContent ? JSON.stringify(initialContent) : null);
      setStatus('saved');
    });

    // Fetch versions
    api.get(`/api/pages/slug/${slug}/versions/`).then((res) => {
      setVersions(res.data);
    }).catch(err => console.error("Could not fetch versions", err));
  }, [slug]);

  const handleSaveDraft = async () => {
    if (!slug) return;
    setIsSaving(true);
    try {
      await api.put(`/api/pages/slug/${slug}/`, {
        title: title,
        slug: slug,
        content: pageData,
        publish: false
      });
      setSavedTitle(title);
      setSavedContentStr(pageData ? JSON.stringify(pageData) : null);
      setStatus('saved');
      setBackupData(pageData);
      addToast('success', 'Draft saved successfully!');
    } catch (err) {
      console.error(err);
      addToast('error', 'Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!slug) return;
    setIsSaving(true);
    try {
      await api.put(`/api/pages/slug/${slug}/`, {
        title: title,
        slug: slug,
        content: pageData,
        publish: true
      });

      // Refresh versions list
      const res = await api.get(`/api/pages/slug/${slug}/versions/`);
      setVersions(res.data);
      setSavedTitle(title);
      setSavedContentStr(pageData ? JSON.stringify(pageData) : null);
      setStatus('saved');
      setBackupData(pageData);
      addToast('success', 'Page published live successfully!');
    } catch (err) {
      console.error(err);
      addToast('error', 'Failed to publish page. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImagesUploaded = (newCells: any[]) => {
    setPageData((prev: any) => {
      const current = prev || { version: 1, rows: [] };
      const newRow = {
        id: Math.random().toString(36).substring(2, 15),
        cells: newCells
      };
      const updated = {
        ...current,
        rows: [...(current.rows || []), newRow]
      };
      if ('cells' in updated) {
        delete updated.cells;
      }
      return updated;
    });
    setStatus('unsaved');
    addToast('info', `${newCells.length} image(s) added to content.`);
  };

  const handleVersionSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const versionId = e.target.value;
    if (!versionId) {
      handleCancelPreview();
      return;
    }
    const selected = versions.find(ver => ver.id.toString() === versionId);
    if (selected) {
      if (!selectedVersion) {
        setBackupData(pageData);
      }
      setSelectedVersion(versionId);
      setPageData(selected.content);
      setStatus('preview');
      addToast('info', 'Loaded past version preview.');
    }
  };

  const handleRestoreVersion = () => {
    setSelectedVersion(null);
    setStatus('unsaved');
    addToast('success', 'Restored version to draft! Save or publish to keep changes.');
  };

  const handleCancelPreview = () => {
    if (backupData) {
      setPageData(backupData);
    }
    setSelectedVersion(null);
    setStatus('saved');
    addToast('info', 'Cancelled version preview.');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);

    if (selectedVersion) return; // Keep preview status if in version preview

    const currentContentStr = pageData ? JSON.stringify(pageData) : null;
    const titleChanged = newTitle !== savedTitle;
    const contentChanged = savedContentStr !== null && currentContentStr !== savedContentStr;

    if (titleChanged || contentChanged) {
      setStatus('unsaved');
    } else {
      setStatus('saved');
    }
  };

  const handleEditorChange = (newData: any) => {
    setPageData(newData);

    if (selectedVersion) return; // Keep preview status if in version preview

    const newStr = newData ? JSON.stringify(newData) : null;

    // Capture initial normalized editor structure baseline if first callback
    if (savedContentStr === null && newStr !== null) {
      setSavedContentStr(newStr);
      setStatus('saved');
      return;
    }

    const titleChanged = title !== savedTitle;
    const contentChanged = newStr !== savedContentStr;

    if (titleChanged || contentChanged) {
      setStatus('unsaved');
    } else {
      setStatus('saved');
    }
  };

  if (!slug) {
    return <div>No slug provided</div>;
  }

  return (
    <div className="editor-page-wrapper">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Top Admin Command Bar */}
      <header className="editor-top-bar">
        <div className="editor-top-left">
          <Link to={`/${slug}`} className="btn-back-link">
            ← View Live Page
          </Link>
          <span className={`status-badge status-${status}`}>
            {status === 'preview' ? '• Previewing Version' : status === 'unsaved' ? '• Unsaved Changes' : '✓ Draft Saved'}
          </span>
        </div>

        <div className="editor-top-center">
          <div className="editor-title-wrapper">
            <input
              id="pageTitle"
              type="text"
              className="editor-title-input"
              value={title}
              placeholder="Page Title..."
              onChange={handleTitleChange}
            />
          </div>
        </div>

        <div className="editor-top-right">
          <BulkImageUpload onImagesUploaded={handleImagesUploaded} />

          {versions.length > 0 && (
            <select
              className="editor-select"
              value={selectedVersion || ''}
              onChange={handleVersionSelect}
            >
              <option value="">Version History ({versions.length})...</option>
              {versions.map(v => (
                <option key={v.id} value={v.id.toString()}>
                  {new Date(v.created_at).toLocaleString()}
                </option>
              ))}
            </select>
          )}

          <button
            id="saveDraftButton"
            className="btn-editor-secondary"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            Save Draft
          </button>

          <button
            id="publishButton"
            className="btn-editor-primary"
            onClick={handlePublish}
            disabled={isSaving}
          >
            Publish
          </button>
        </div>
      </header>

      {/* Version Preview Banner */}
      {selectedVersion && (
        <div className="version-preview-banner">
          <span>
            Previewing version from{' '}
            <strong>
              {new Date(
                versions.find(v => v.id.toString() === selectedVersion)?.created_at
              ).toLocaleString()}
            </strong>
          </span>
          <div className="version-preview-actions">
            <button className="btn-editor-primary" onClick={handleRestoreVersion}>
              Restore as Draft
            </button>
            <button className="btn-editor-outline" onClick={handleCancelPreview}>
              Cancel Preview
            </button>
          </div>
        </div>
      )}

      {/* Page Header (read-only header element) */}
      <div id="page-header">
        <Editor
          cellPlugins={cellPlugins}
          value={headder?.content}
          readOnly
        />
      </div>

      {/* Main Content Area */}
      <main id="page-content">
        <Editor
          cellPlugins={cellPlugins}
          value={pageData}
          onChange={handleEditorChange}
        />
      </main>
    </div>
  );
}


